import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface UsePendingInvitationsOptions {
  companyId?: string;
  pageSize?: number;
  enabled?: boolean;
}

export const PENDING_INVITATIONS_QUERY_KEY = "pending-invitations";

export const usePendingInvitations = (
  options: UsePendingInvitationsOptions,
): {
  invitations: UserManagement_V1.InvitationSummary[];
  isLoading: boolean;
  totalInvitations: number;
  hasMore: boolean | undefined;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
} => {
  const { companyId, pageSize = 500, enabled = true } = options;
  const userManagementService = useUserManagementServiceV1();

  const queryKey = useMemo(
    () => [PENDING_INVITATIONS_QUERY_KEY, companyId, { pageSize }],
    [companyId, pageSize],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      if (!companyId) throw new Error("Company ID is required");

      const result =
        await userManagementService.getPendingInvitationsForCompany(
          companyId,
          pageParam,
          pageSize,
        );

      return {
        invitations: result,
        nextPage: result.length === pageSize ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (
      queryData: InfiniteData<{
        invitations: UserManagement_V1.InvitationSummary[];
      }>,
    ) => ({
      pages: queryData.pages,
      pageParams: queryData.pageParams,
      allInvitations: queryData.pages.flatMap((page) => page.invitations),
    }),
  });

  return useMemo(
    () => ({
      invitations: data?.allInvitations ?? [],
      isLoading,
      totalInvitations: data?.allInvitations.length ?? 0,
      hasMore: hasNextPage,
      isFetching,
      isFetchingNextPage,
      fetchNextPage,
      refetch,
    }),
    [
      data,
      isLoading,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      fetchNextPage,
      refetch,
    ],
  );
};
